import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import React, { useRef } from "react";

import {
    CirclesPromoBlock,
    ImageChooserBlock as ImageChooserBlockType,
} from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { ButtonPrimary } from "../Clickables";
import { TRACKED_SECTION_ATTR } from "../PageSkeleton/TrackedStreamField";

import breakpoints from "../../styles/breakpoint.module.scss";
import styles from "./CirclesPromo.module.scss";

gsap.registerPlugin(ScrollTrigger);

const REQUIRED_IMAGE_COUNT = 7;

// Alternating positive/negative speeds create depth by moving columns in opposite directions
// Desktop distance produces ~100px max movement, mobile produces ~40px max movement
const PARALLAX_SPEEDS = [0.12, -0.08, 0.08, -0.1, 0.1];
const PARALLAX_DISTANCE_DESKTOP = 850;
const PARALLAX_DISTANCE_MOBILE = 375;

type ImageItem = { id: string; type: string; value: ImageChooserBlockType };
function buildColumns(images: ImageItem[]) {
    return [
        [images[0]],
        [images[1], images[2]],
        [images[3]],
        [images[4], images[5]],
        [images[6]],
    ];
}

interface Props {
    value: CirclesPromoBlock;
}

// Expand a TrackedStreamField wrapper into the rendered DOM elements it
// contains. Nested TrackedStreamFields (e.g. ReusableContentBlock) produce
// stacked `data-track-section` `display: contents` wrappers; recurse through
// them until we reach the leaf block's rendered root, which is the element
// that has a real box and can paint a background.
function expandInnerBlocks(wrapper: HTMLElement): HTMLElement[] {
    const trackedChildren = Array.from(wrapper.children).filter(
        (c): c is HTMLElement =>
            c instanceof HTMLElement && c.hasAttribute(TRACKED_SECTION_ATTR),
    );
    if (trackedChildren.length > 0) {
        return trackedChildren.flatMap(expandInnerBlocks);
    }
    return wrapper.firstElementChild instanceof HTMLElement
        ? [wrapper.firstElementChild]
        : [];
}

// NOTE: Relies on the StreamField sibling layout. Top-level blocks are siblings
// of each other, each wrapped in a `display: contents` div by TrackedStreamField
// for analytics. ReusableContentBlock renders a nested TrackedStreamField, so
// its inner blocks live one level deeper. If a future change introduces a
// non-contents wrapper or a third nesting level, this traversal must be revisited.
function collectSentinelTargets(root: HTMLElement): HTMLElement[] {
    const targets: HTMLElement[] = [];

    // If the root sits inside a TrackedStreamField wrapper, traverse at the
    // wrapper level so we see other blocks' wrappers as siblings.
    const blockEl =
        root.parentElement?.hasAttribute(TRACKED_SECTION_ATTR) === true
            ? root.parentElement
            : root;

    let sibling = blockEl.previousElementSibling;
    while (sibling) {
        if (sibling instanceof HTMLElement) {
            const expanded = sibling.hasAttribute(TRACKED_SECTION_ATTR)
                ? expandInnerBlocks(sibling)
                : [sibling];

            // Walking back from CirclesPromo, so iterate expanded blocks in
            // reverse — the rightmost inner block of the previous wrapper is
            // closer to us than the leftmost.
            for (let i = expanded.length - 1; i >= 0; i--) {
                const block = expanded[i];
                if (block.hasAttribute("data-bg-transition-sentinel")) {
                    return targets;
                }
                targets.push(block);
            }
        }
        sibling = sibling.previousElementSibling;
    }
    return [];
}

export const CirclesPromo = ({ value }: Props) => {
    const rootRef = useRef<HTMLElement>(null);
    const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
    const sentinelTargetsRef = useRef<HTMLElement[]>([]);

    useGSAP(
        () => {
            if (!rootRef.current) return;

            const themeKey = value.destination_bg_color ?? "salmon";
            const destinationBg = `var(--${themeKey})`;
            rootRef.current.style.setProperty(
                "--destination-bg",
                destinationBg,
            );

            sentinelTargetsRef.current = [
                rootRef.current,
                ...collectSentinelTargets(rootRef.current),
            ];
            sentinelTargetsRef.current.forEach((el) => {
                el.style.setProperty("--destination-bg", destinationBg);
                el.setAttribute("data-bg-transition-target", "");
            });

            const activateTargets = () => {
                sentinelTargetsRef.current.forEach((el) => {
                    el.setAttribute("data-bg-transition-active", "");
                });
            };

            const deactivateTargets = () => {
                sentinelTargetsRef.current.forEach((el) => {
                    el.removeAttribute("data-bg-transition-active");
                });
            };

            const mobileBreakpoint = parseInt(breakpoints.mobile) - 1;
            const mm = gsap.matchMedia();

            const root = rootRef.current;
            ScrollTrigger.create({
                trigger: root,
                // Equivalent to "top center", but never before 10px of scroll —
                // otherwise the transition fires on load when the section already
                // sits at/above the viewport center. start returns an absolute
                // scroll position; we take whichever is further down the page.
                start: () => {
                    const topCenter =
                        root.getBoundingClientRect().top +
                        window.scrollY -
                        window.innerHeight / 2;
                    return Math.max(topCenter, 10);
                },
                onEnter: activateTargets,
                onLeaveBack: deactivateTargets,
            });

            mm.add(
                {
                    mobile: `(max-width: ${mobileBreakpoint}px) and (prefers-reduced-motion: no-preference)`,
                    desktop: `(min-width: ${mobileBreakpoint + 1}px) and (prefers-reduced-motion: no-preference)`,
                },
                (context) => {
                    const { mobile } = context.conditions!;
                    const distance = mobile
                        ? PARALLAX_DISTANCE_MOBILE
                        : PARALLAX_DISTANCE_DESKTOP;

                    // Parallax: runs for the full scroll duration
                    const tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: rootRef.current,
                            start: "top bottom",
                            end: "bottom top",
                            scrub: true,
                        },
                    });

                    columnRefs.current.forEach((col, i) => {
                        if (!col) return;

                        const speed = PARALLAX_SPEEDS[i] || 0;

                        tl.fromTo(
                            col,
                            { y: -distance * speed },
                            { y: distance * speed, ease: "none" },
                            0,
                        );
                    });
                },
            );

            return () => {
                mm.revert();
                sentinelTargetsRef.current.forEach((el) => {
                    el.removeAttribute("data-bg-transition-target");
                    el.removeAttribute("data-bg-transition-active");
                    el.style.removeProperty("--destination-bg");
                });
                sentinelTargetsRef.current = [];
            };
        },
        { scope: rootRef, dependencies: [value.destination_bg_color] },
    );

    const images = value.images ?? [];
    const columns =
        images.length >= REQUIRED_IMAGE_COUNT ? buildColumns(images) : [];

    return (
        <section className={styles.root} ref={rootRef}>
            <div className={styles.imageGrid}>
                {columns.map((colImages, colIndex) => (
                    <div
                        key={colIndex}
                        className={styles.column}
                        ref={(el) => {
                            columnRefs.current[colIndex] = el;
                        }}
                    >
                        {colImages.map((item) => (
                            <div key={item.id} className={styles.imageCard}>
                                {item.type === "image" && (
                                    <ImageChooserBlock
                                        value={item.value}
                                        attrs={{}}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                ))}
            </div>

            <div className={styles.textBlock}>
                {value.headline && (
                    <h2 className={styles.headline}>{value.headline}</h2>
                )}
                {value.subhead && (
                    <p className={styles.subhead}>{value.subhead}</p>
                )}
                {value.cta && <ButtonPrimary simpleLink={value.cta} />}
            </div>
        </section>
    );
};
